/**
 * TonemapShader - TSL tonemapping node with support for all THREE.js tone mapping modes.
 */

import * as THREE from 'three';
import { float, Fn, If, toneMapping, vec3, vec4 } from 'three/tsl';

import type { ToneMapping } from 'three';
import type { Node, NodeBuilder, UniformNode } from 'three/webgpu';

/**
 * Tonemapping types matching THREE.js constants
 */
export const TonemappingType = {
  None: THREE.NoToneMapping,
  Linear: THREE.LinearToneMapping,
  Reinhard: THREE.ReinhardToneMapping,
  Cineon: THREE.CineonToneMapping,
  ACESFilmic: THREE.ACESFilmicToneMapping,
  AgX: THREE.AgXToneMapping,
  Neutral: THREE.NeutralToneMapping,
} as const;

/**
 * Custom tonemap shader node using TSL and native ToneMappingNode
 * @param colorInput - Input color node
 * @param exposureUniform - Exposure uniform
 * @param tonemapTypeUniform - Tonemapping type uniform
 * @returns Tonemapped color output
 */
export const createTonemapNode = (
  colorInput: Node,
  exposureUniform: UniformNode<'float', number>,
  tonemapTypeUniform: UniformNode<'float', number>
) => {
  return Fn((_builder: NodeBuilder) => {
    const color = vec3(colorInput as Node<'vec3'>);
    const exposure = float(exposureUniform);
    const tonemapType = float(tonemapTypeUniform);

    // Helper to apply specific tonemapping (exposure is applied internally by toneMapping node)
    const applyToneMap = (type: ToneMapping) => toneMapping(type, exposure, color);

    // NoToneMapping: pass through colour unchanged (no exposure, no tonemapping)
    // Linear: apply exposure only via toneMapping
    // Others: apply exposure + tonemapping
    const result = vec3(0).toVar();

    If(tonemapType.equal(float(THREE.NoToneMapping)), () => {
      result.assign(color);
    }).ElseIf(tonemapType.equal(float(THREE.LinearToneMapping)), () => {
      result.assign(vec3(applyToneMap(THREE.LinearToneMapping)));
    }).ElseIf(tonemapType.equal(float(THREE.ReinhardToneMapping)), () => {
      result.assign(vec3(applyToneMap(THREE.ReinhardToneMapping)));
    }).ElseIf(tonemapType.equal(float(THREE.CineonToneMapping)), () => {
      result.assign(vec3(applyToneMap(THREE.CineonToneMapping)));
    }).ElseIf(tonemapType.equal(float(THREE.ACESFilmicToneMapping)), () => {
      result.assign(vec3(applyToneMap(THREE.ACESFilmicToneMapping)));
    }).ElseIf(tonemapType.equal(float(THREE.AgXToneMapping)), () => {
      result.assign(vec3(applyToneMap(THREE.AgXToneMapping)));
    }).Else(() => {
      result.assign(vec3(applyToneMap(THREE.NeutralToneMapping)));
    });

    // Explicitly convert to vec3 before constructing vec4 to avoid TSL type issues
    return vec4(vec3(result), 1.0);
  })();
};
